<template>
  <div class="w-full video">
    <video
      ref="videoRef"
      :src="videoUrl"
      crossorigin="anonymous"
      preload="auto"
      class="block w-full h-[468px] rounded-[6px] bg-back-4"
      @timeupdate="onTimeupdate"
      @loadeddata="onLoaded"
      @end="onEnd"
      @error="onError"
    />
    <div class="range">
      <span class="left">
        {{ displayStartTime }}
      </span>
      <span class="right"> / {{ displayEndTime }} </span>
    </div>
    <div class="controls">
      <div class="play-control">
        <iconpark-icon
          class="icon-play"
          :name="videoMeta.paused ? 'play' : 'pause'"
          @click="togglePlay"
        />
      </div>

      <ImgList
        v-if="videoMeta.duration > 0"
        :list="frames"
        :maxDuration="maxDuration"
        @change="onPointerChange"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import ImgList from './imgList.vue'
import type { IVideoMeta } from './types'
import { useTime } from '~/hooks/useTime'

const props = defineProps<{ videoUrl: string; maxDuration: number }>()
const emit = defineEmits(['change', 'onload', 'error'])

const fileUrl = ref<string>('')
const videoRef = ref<HTMLVideoElement>()
const frames = ref<any[]>(new Array(14))
const canvas = document.createElement('canvas')
const { $t } = useNuxtApp()
const { formatDuration } = useTime()

const videoMeta = reactive<IVideoMeta>({
  duration: 0,
  range: { start_time: 0, end_time: 10 },
  currentTime: 0,
  paused: true,
})

const displayStartTime = computed(() =>
  formatDuration(Math.ceil(videoMeta.currentTime) * 1000)
)

const displayEndTime = computed(() =>
  formatDuration(Math.ceil(videoMeta.duration) * 1000)
)

const onEnd = () => pause()

const play = () => {
  if (!videoMeta.paused) {
    return
  }
  videoRef.value?.play()
  videoMeta.paused = false
}

const pause = () => {
  if (videoMeta.paused) {
    return
  }
  videoRef.value?.pause()
  videoMeta.paused = true
}

const onTimeupdate = () => {
  if (!videoRef.value) {
    return
  }
  if (videoMeta.paused) {
    return
  }
  if (
    videoRef.value.currentTime > +videoMeta.range?.end_time ||
    videoRef.value.currentTime < +videoMeta.range?.start_time
  ) {
    videoRef.value.currentTime = +videoMeta.range?.start_time
    videoMeta.currentTime = videoRef.value.currentTime
    pause()
    return
  }

  videoMeta.currentTime = videoRef.value.currentTime
}

const togglePlay = () => {
  if (videoMeta.paused) {
    play()
  } else {
    pause()
  }
}

provide('dynamic-cover-video-control', {
  videoMeta,
  togglePlay,
})

const onPointerChange = (range: [number, number]) => {
  if (videoRef.value) {
    videoRef.value.currentTime = +range[0]
    videoMeta.range = {
      start_time: +range[0],
      end_time: +range[1],
    }
    emit('change', { start_time: +range[0], end_time: +range[1] })
  }
}

const onLoaded = () => {
  videoMeta.duration = +(videoRef.value?.duration || 0)
  videoMeta.range = {
    start_time: +0,
    end_time: Math.min(+videoMeta.duration, 10),
  }
  emit('onload')
}
const onError = () => {
  message.error($t('shi-pin-jia-zai-shi-bai'))
  emit('error')
}

const getFrams = () => {
  const video: any = document.createElement('video')
  video.setAttribute('crossorigin', 'anonymous')

  let step = 0
  let index = 0
  const context = canvas.getContext('2d')
  video.onloadeddata = () => {
    canvas.width = video.videoWidth
    canvas.height = video.videoHeight
    step = video?.duration / 14
    video.currentTime = 0.1
  }
  video.onseeked = () => {
    context?.clearRect(0, 0, canvas.width, canvas.height)
    context?.drawImage(video, 0, 0, canvas.width, canvas.height)
    const img = canvas.toDataURL('image/jpeg')
    index === 0 ? frames.value.fill(img) : (frames.value[index] = img)
    if (video.currentTime < video?.duration - step) {
      video.currentTime += step
      index++
    }
  }
  video.src = props.videoUrl
  video.load()
}

getFrams()

defineExpose({
  fileUrl,
})
</script>

<style lang="scss" scoped>
.icon {
  color: var(--purple-6);
}

.range {
  margin-top: 6px;
  font-size: 12px;
  line-height: 1;
  .left {
    font-weight: 500;
  }
  .right {
    color: var(--text-1-60);
  }
}

.controls {
  width: 100%;
  display: flex;
  justify-content: center;
  margin: 20px 0 6px;
  .play-control {
    width: 56px;
    font-size: 18px;
    display: flex;
    justify-content: center;
    align-items: center;
    background: #f3f4ff;
    border-radius: 6px 0px 0px 6px;
  }
}
</style>
